<template>
  <div
    class="map"
    :class="{ active, ready }"
    @mouseleave="stop()"
    @click="start()"
    @keyup.esc="stop()"
  >
    <LazyAtomsButton
      v-if="dynamic && active"
      class="map-close"
      :icon="{ name: 'x-mark' }"
      plain
      title="Close map"
      aria-label="Close map"
      @click="stop()"
    />
    <div v-if="dynamic" ref="el" class="map-wrapper" />
    <LazyAtomsImage v-else :image="{ src: url }" class="map-image" />
  </div>
</template>

<script lang="ts" setup>
import logomark from '~/assets/icons/logomark.svg?url_encode'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

const props = defineProps({
  lon: {
    type: Number,
    default: 18.2,
  },
  lat: {
    type: Number,
    default: 86.1,
  },
  zoom: {
    type: Number,
    default: 4.2,
  },
  width: {
    type: Number,
    default: 1000,
  },
  height: {
    type: Number,
    default: 600,
  },
  dynamic: {
    type: Boolean,
    default: true,
  },
})

const accessToken =
  'pk.eyJ1IjoiYWFkZ3JhbnQiLCJhIjoiY2tpYm5lZnllMTEwYTJ5bGIxemxvZGdwbSJ9.lsN9PAuXn-_BwYtvRKWljA'
const el = ref()
const username = 'aadgrant'
const dgh = {
  lat: 88.4877427,
  lon: 27.0835707,
}
const style = 'cld06uj4h003014nzs8u6ij17'
const overlay = `pin-l+392F60(${dgh.lat},${dgh.lon})`
const dpi = '@2x'
const url = `https://api.mapbox.com/styles/v1/${username}/${style}/static/${overlay}/${props.lat},${props.lon},${props.zoom}/${props.width}x${props.height}${dpi}?access_token=${accessToken}`

const active = ref(false)
const ready = ref(true)

const start = () => {
  if (!ready.value) return
  cznScroll(el)
  active.value = true
  ready.value = false
}
const stop = () => {
  active.value = false
  setTimeout(() => (ready.value = true), 500)
}

onMounted(() => {
  if (!process.client || !props.dynamic) return

  mapboxgl.accessToken = accessToken
  const map = new mapboxgl.Map({
    container: el.value,
    style: `mapbox://styles/${username}/${style}`,
    center: [props.lat, props.lon],
    zoom: props.zoom,
  })

  const img = new Image(291, 158)
  img.src = logomark

  map.on('load', () => {
    map.addSource('points', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [dgh.lat, dgh.lon],
            },
            properties: {
              title: 'Dr Graham’s Homes',
            },
          },
        ],
      },
    })
    map.addLayer({
      id: 'points',
      type: 'symbol',
      source: 'points',

      layout: {
        'icon-image': 'logomark',
        'icon-size': 0.2,
        'text-field': ['get', 'title'],
        'text-font': ['League Spartan Bold'],
        'text-offset': [0, 1.25],
        'text-anchor': 'top',
      },
      paint: {
        'text-color': '#392F60',
      },
    })
  })
  img.onload = () => map.addImage('logomark', img)
})

watchEffect(() => {
  handleEventListener<'keyup'>(active.value, 'keyup', (e) => {
    switch (e.code) {
      case 'Escape':
        stop()
        break
    }
  })
})
</script>

<style lang="scss">
.map {
  position: relative;
  width: 100%;
  height: 100%;

  &.ready::before {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: black;
    opacity: 0;
    color: white;
    z-index: 10;
    content: 'Click to explore';
    transition: czn.transition(var(--transition), (opacity));
    pointer-events: none;
  }

  &.ready::before {
    cursor: pointer;
    opacity: 0.4;
    pointer-events: all;
  }

  &-wrapper {
    width: 100%;
    padding-bottom: 60%;
    max-height: 90vh;
    max-height: calc(100vh - 5rem);
  }

  &-close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 100;
    width: 4em;
    height: 4em;
    margin: auto;
    z-index: 50;
    color: var(--background);

    &:hover,
    &:focus,
    &:active {
      color: var(--purple);
    }
  }

  .map {
    &-image {
      width: 100%;
      border-radius: var(--br-items-lg);
    }
  }

  .marker {
    background-size: cover;
    background: var(--purple);
    cursor: pointer;
  }
}
</style>
